import { useMemo } from 'react';

import { partRows, type ModelDoc, type ModelPart, type ModelShape } from '../../lib/model-doc.js';

/**
 * Front-view (XY) silhouette of a model, drawn when the engine is not built.
 * Each part is approximated by its bounding box (no rotation, ops or arrays),
 * filled with its material color — enough to see proportions and to select.
 */

/** Width, height and the box center's offset from the part origin. */
function extent(shape: ModelShape | undefined): [number, number, number?, number?] {
  const n = (k: string, d: number) => (typeof shape?.[k] === 'number' ? (shape[k] as number) : d);
  const v = (k: string) => (Array.isArray(shape?.[k]) ? (shape![k] as number[]) : null);
  switch (shape?.type ?? 'empty') {
    case 'box':
    case 'cube': {
      const s = v('size') ?? [1, 1, 1];
      return [s[0] ?? 1, s[1] ?? 1];
    }
    case 'sphere':
      return [n('radius', 0.5) * 2, n('radius', 0.5) * 2];
    case 'capsule':
      return [n('radius', 0.25) * 2, n('height', 1)];
    case 'cylinder': {
      const r = Math.max(n('radiusTop', n('radius', 0.5)), n('radiusBottom', n('radius', 0.5)));
      return [r * 2, n('height', 1)];
    }
    case 'cone':
      return [n('radius', 0.5) * 2, n('height', 1)];
    case 'torus':
      return [(n('radius', 0.5) + n('tube', 0.15)) * 2, n('tube', 0.15) * 2];
    case 'plane': {
      const s = v('size') ?? [1, 1];
      return [s[0] ?? 1, 0.02];
    }
    case 'extrude':
    case 'lathe': {
      const pts = (Array.isArray(shape?.[shape.type === 'extrude' ? 'polygon' : 'points']) ? shape![shape!.type === 'extrude' ? 'polygon' : 'points'] : []) as number[][];
      if (!pts.length) return [0.6, 0.6];
      const xs = pts.map((p) => p[0] ?? 0);
      const ys = pts.map((p) => p[1] ?? 0);
      if (shape?.type === 'lathe') {
        const r = Math.max(...xs.map(Math.abs));
        return [r * 2, Math.max(...ys) - Math.min(...ys), 0, (Math.max(...ys) + Math.min(...ys)) / 2];
      }
      return [Math.max(...xs) - Math.min(...xs), Math.max(...ys) - Math.min(...ys), (Math.max(...xs) + Math.min(...xs)) / 2, (Math.max(...ys) + Math.min(...ys)) / 2];
    }
    case 'blob': {
      const balls = (Array.isArray(shape?.balls) ? shape!.balls : []) as { center?: number[]; radius?: number }[];
      if (!balls.length) return [0.6, 0.6];
      const minX = Math.min(...balls.map((b) => (b.center?.[0] ?? 0) - (b.radius ?? 0.5)));
      const maxX = Math.max(...balls.map((b) => (b.center?.[0] ?? 0) + (b.radius ?? 0.5)));
      const minY = Math.min(...balls.map((b) => (b.center?.[1] ?? 0) - (b.radius ?? 0.5)));
      const maxY = Math.max(...balls.map((b) => (b.center?.[1] ?? 0) + (b.radius ?? 0.5)));
      return [maxX - minX, maxY - minY, (maxX + minX) / 2, (maxY + minY) / 2];
    }
    case 'empty':
    case 'group':
      return [0, 0];
    default:
      return [0.6, 0.6];
  }
}

function rgb(color: unknown): string {
  const c = Array.isArray(color) ? (color as number[]) : [0.8, 0.8, 0.8];
  const ch = (x: number | undefined) => Math.round(Math.min(1, Math.max(0, x ?? 0.8)) * 255);
  return `rgb(${ch(c[0])} ${ch(c[1])} ${ch(c[2])})`;
}

export function ModelSchematic({
  doc,
  selected,
  wireframe,
  onSelect,
}: {
  doc: ModelDoc;
  selected: string | null;
  wireframe: boolean;
  onSelect: (partPath: string | null) => void;
}) {
  const boxes = useMemo(() => {
    const rows = partRows(doc);
    const offset = new Map<string, [number, number, number]>();
    const scaleOf = (p: ModelPart) => p.transform?.scale ?? [1, 1, 1];
    return rows
      .map((row) => {
        const parent = row.partPath.includes('/') ? row.partPath.slice(0, row.partPath.lastIndexOf('/')) : null;
        const base = (parent && offset.get(parent)) || [0, 0, 1];
        const pos = row.part.transform?.position ?? [0, 0, 0];
        const scale = scaleOf(row.part);
        const at: [number, number, number] = [base[0] + pos[0] * base[2], base[1] + pos[1] * base[2], base[2]];
        offset.set(row.partPath, at);
        const [w, h, ox = 0, oy = 0] = extent(row.part.shape);
        const material = doc.materials?.[row.part.material ?? 'default'];
        return {
          path: row.partPath,
          hidden: Boolean(row.part.hidden),
          x: at[0] + ox * (scale[0] ?? 1),
          y: at[1] + oy * (scale[1] ?? 1),
          w: w * (scale[0] ?? 1),
          h: h * (scale[1] ?? 1),
          poly:
            row.part.shape?.type === 'extrude' && Array.isArray(row.part.shape.polygon)
              ? (row.part.shape.polygon as number[][]).map((p) => [at[0] + (p[0] ?? 0) * (scale[0] ?? 1), at[1] + (p[1] ?? 0) * (scale[1] ?? 1)])
              : null,
          round: ['sphere', 'capsule', 'torus'].includes(row.part.shape?.type ?? ''),
          cone: row.part.shape?.type === 'cone',
          fill: rgb(material?.color),
          opacity: Array.isArray(material?.color) ? ((material!.color as number[])[3] ?? 1) : 1,
        };
      })
      .filter((b) => b.w > 0 && b.h > 0);
  }, [doc]);

  const bounds = useMemo(() => {
    if (!boxes.length) return { minX: -1, maxX: 1, minY: -1, maxY: 1 };
    return {
      minX: Math.min(...boxes.map((b) => b.x - b.w / 2)),
      maxX: Math.max(...boxes.map((b) => b.x + b.w / 2)),
      minY: Math.min(...boxes.map((b) => b.y - b.h / 2)),
      maxY: Math.max(...boxes.map((b) => b.y + b.h / 2)),
    };
  }, [boxes]);

  const span = Math.max(bounds.maxX - bounds.minX, bounds.maxY - bounds.minY, 0.5) * 1.35;
  const cx = (bounds.minX + bounds.maxX) / 2;
  const cy = (bounds.minY + bounds.maxY) / 2;
  const unit = span / 100;

  return (
    <svg
      className="schematic model-schematic"
      viewBox={`${cx - span / 2} ${-cy - span / 2} ${span} ${span}`}
      preserveAspectRatio="xMidYMid meet"
      onClick={(e) => (e.target as Element).tagName === 'svg' && onSelect(null)}
      strokeWidth={unit * 0.35}
    >
      <line x1={cx - span} x2={cx + span} y1={0} y2={0} className="axis-x" strokeWidth={unit * 0.2} />
      <line y1={-cy - span} y2={-cy + span} x1={0} x2={0} className="axis-y" strokeWidth={unit * 0.2} />
      {boxes.map((b) => {
        const isSel = selected === b.path || (selected !== null && b.path.startsWith(`${selected}/`));
        const common = {
          className: `schem-part${isSel ? ' is-selected' : ''}${b.hidden ? ' is-hidden' : ''}${wireframe ? ' is-wire' : ''}`,
          style: { fill: wireframe ? 'none' : b.fill, fillOpacity: b.hidden ? 0.08 : b.opacity * 0.9 },
          onClick: (e: React.MouseEvent) => {
            e.stopPropagation();
            onSelect(b.path);
          },
        };
        if (b.poly) {
          return (
            <polygon key={b.path} {...common} points={b.poly.map((p) => `${p[0]},${-(p[1] ?? 0)}`).join(' ')}>
              <title>{b.path}</title>
            </polygon>
          );
        }
        if (b.cone) {
          return (
            <polygon
              key={b.path}
              {...common}
              points={`${b.x - b.w / 2},${-(b.y - b.h / 2)} ${b.x + b.w / 2},${-(b.y - b.h / 2)} ${b.x},${-(b.y + b.h / 2)}`}
            >
              <title>{b.path}</title>
            </polygon>
          );
        }
        return (
          <rect
            key={b.path}
            {...common}
            x={b.x - b.w / 2}
            y={-(b.y + b.h / 2)}
            width={b.w}
            height={b.h}
            rx={b.round ? Math.min(b.w, b.h) / 2 : unit * 0.4}
          >
            <title>{b.path}</title>
          </rect>
        );
      })}
    </svg>
  );
}
